import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Kpi, Empty } from "@/components/ui-bits";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Megaphone, Rocket, Handshake, ChatCircleDots, ArrowClockwise, EnvelopeSimple, PhoneCall, WhatsappLogo, UsersThree, DownloadSimple, FilePdf, MicrosoftExcelLogo,
} from "@phosphor-icons/react";

const KIND_META = {
  demo: { label: "Demo", icon: Megaphone, cls: "bg-blue-100 text-blue-800" },
  trial: { label: "Trial", icon: Rocket, cls: "bg-emerald-100 text-emerald-800" },
  partner: { label: "Partner", icon: Handshake, cls: "bg-purple-100 text-purple-800" },
  contact: { label: "Contact", icon: ChatCircleDots, cls: "bg-amber-100 text-amber-800" },
};

// Normalize an Indian phone for wa.me / tel: (defaults to +91 when no country code).
const waNumber = (p) => {
  const d = (p || "").replace(/\D/g, "");
  if (!d) return "";
  return d.length === 10 ? `91${d}` : d;
};
const waText = (name, kind) => {
  const first = (name || "").split(" ")[0] || "there";
  const msgs = {
    trial: `Hi ${first}, 👋

Welcome to RecoverOS! 🎉
Thanks for signing up.

Our onboarding specialist will call you shortly.

📁 Please keep your Receivables Excel file ready—we'll upload it together and show you where your money is stuck.

Login: https://app.recoveros.in/login

See you soon!
Team RecoverOS
https://recoveros.in`,
    demo: `Hi ${first}, thanks for your interest in a RecoverOS demo. I'd love to show you how it surfaces the money stuck in your portfolio and speeds up recovery. Let me know a convenient time to connect.`,
    partner: `Hi ${first}, thanks for your interest in the RecoverOS partner program. I'd be happy to walk you through the referral and revenue-share and how we can work together. Let me know a convenient time to connect.`,
    contact: `Hi ${first}, thanks for reaching out to RecoverOS. I'd be happy to help with your query — let me know a convenient time to connect.`,
  };
  return encodeURIComponent(msgs[kind] || msgs.trial);
};

const STATUSES = ["new", "contacted", "qualified", "high_intent", "converted", "closed"];
const STATUS_CLS = {
  new: "bg-blue-100 text-blue-800",
  contacted: "bg-amber-100 text-amber-800",
  qualified: "bg-indigo-100 text-indigo-800",
  high_intent: "bg-rose-100 text-rose-800",
  converted: "bg-emerald-100 text-emerald-800",
  closed: "bg-secondary text-muted-foreground",
};
const TEMP_CLS = {
  Cold: "bg-slate-100 text-slate-600",
  Warm: "bg-amber-100 text-amber-800",
  Hot: "bg-orange-100 text-orange-800",
  "High Intent": "bg-rose-100 text-rose-800",
  "Sales Ready": "bg-emerald-100 text-emerald-800",
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [counts, setCounts] = useState({ kind: {}, status: {} });
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("");
  const [updating, setUpdating] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(null);

  const exportLeads = async (fmt) => {
    setExporting(fmt);
    try {
      const res = await api.get("/leads/export", {
        params: {
          format: fmt, kind: kind || undefined, status: status || undefined,
          date_from: dateFrom || undefined, date_to: dateTo || undefined,
        },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `recoveros_leads.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Leads exported as ${fmt.toUpperCase()}`);
    } catch (e) {
      toast.error("Export failed");
    } finally { setExporting(null); }
  };

  const syncSignups = async () => {
    setSyncing(true);
    try {
      const dry = await api.post("/admin/backfill-signup-leads?dry_run=true");
      const n = dry.data.leads_to_create || 0;
      if (n === 0) {
        toast.success("All trial signups are already in your Leads inbox.");
        return;
      }
      const ok = window.confirm(
        `${n} self-signup${n === 1 ? "" : "s"} are not yet in Leads.\n\n` +
        `Add them as Leads and send each a catch-up Welcome email now?`);
      if (!ok) return;
      const res = await api.post("/admin/backfill-signup-leads?dry_run=false&send_welcome=true");
      toast.success(`Added ${res.data.leads_created} lead(s); sent ${res.data.welcome_emails_sent} welcome email(s).`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  const load = () => {
    setLoading(true);
    api.get("/leads", { params: { kind: kind || undefined, status: status || undefined } })
      .then((r) => {
        setLeads(r.data.leads || []);
        setCounts({ kind: r.data.counts_kind || {}, status: r.data.counts_status || {} });
      })
      .catch(() => toast.error("Could not load leads"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [kind, status]);
  useEffect(() => { api.get("/admin/events/summary").then((r) => setFunnel(r.data)).catch(() => {}); }, []);

  const changeStatus = async (lead, newStatus) => {
    if (newStatus === lead.status) return;
    setUpdating(lead.lead_id);
    try {
      await api.patch(`/leads/${lead.lead_id}/status`, { status: newStatus });
      toast.success(`Marked ${lead.name} as ${newStatus}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update lead");
    } finally { setUpdating(null); }
  };

  const total = leads.length;

  return (
    <div data-testid="leads-page">
      <PageHeader title="Leads Inbox" subtitle="Demo, trial, partner and contact requests captured from your marketing site">
        <Button variant="outline" size="sm" onClick={syncSignups} disabled={syncing} className="gap-1 h-9" data-testid="leads-sync-signups">
          <UsersThree size={15} /> {syncing ? "Syncing…" : "Sync Trial Signups"}
        </Button>
        <Button variant="ghost" size="sm" onClick={load} className="gap-1 h-9" data-testid="leads-refresh">
          <ArrowClockwise size={15} /> Refresh
        </Button>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi label="Demo Requests" value={counts.kind.demo || 0} testId="leads-kpi-demo" />
        <Kpi label="Trial Requests" value={counts.kind.trial || 0} accent="text-emerald-600" testId="leads-kpi-trial" />
        <Kpi label="Partner Leads" value={counts.kind.partner || 0} testId="leads-kpi-partner" />
        <Kpi label="New (uncontacted)" value={counts.status.new || 0} accent="text-[#0B3D91]" testId="leads-kpi-new" />
      </div>

      {/* Welcome modal funnel (last 30 days) */}
      {funnel && (
        <div className="border border-border bg-card px-5 py-3 mb-6 flex flex-wrap items-center gap-x-8 gap-y-2" data-testid="welcome-funnel">
          <span className="label-caps">Welcome Modal · 30d</span>
          <span className="text-sm"><span className="font-bold mono">{funnel.modal_views}</span> <span className="text-muted-foreground">views</span></span>
          <span className="text-sm"><span className="font-bold mono">{funnel.modal_cta_clicks}</span> <span className="text-muted-foreground">CTA clicks</span></span>
          <span className="text-sm"><span className="font-bold mono text-[#16A34A]">{funnel.modal_ctr}%</span> <span className="text-muted-foreground">click-through</span></span>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {[["", "All Types"], ...Object.keys(KIND_META).map((k) => [k, KIND_META[k].label])].map(([k, lbl]) => (
          <button key={k || "all"} onClick={() => setKind(k)}
            className={`px-3 py-1 text-xs font-semibold rounded-sm border transition-colors ${kind === k ? "bg-[#0B3D91] text-white border-[#0B3D91]" : "bg-card border-border hover:border-[#0B3D91]/40"}`}
            data-testid={`leads-filter-kind-${k || "all"}`}>
            {lbl}
          </button>
        ))}
        <div className="ml-auto w-44">
          <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
            <SelectTrigger className="h-8 rounded-sm text-xs" data-testid="leads-filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => (<SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Export bar: date-range + status filtered download */}
      <div className="flex items-center gap-2 mb-4 flex-wrap border border-border bg-secondary/30 rounded-sm px-3 py-2" data-testid="leads-export-bar">
        <DownloadSimple size={16} className="text-[#0B3D91]" />
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mr-1">Export</span>
        <label className="text-xs text-muted-foreground">From</label>
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
          className="h-8 rounded-sm border border-border bg-card px-2 text-xs" data-testid="leads-export-from" />
        <label className="text-xs text-muted-foreground">To</label>
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
          className="h-8 rounded-sm border border-border bg-card px-2 text-xs" data-testid="leads-export-to" />
        <span className="text-[11px] text-muted-foreground">
          {status ? `status: ${status.replace("_", " ")}` : "all statuses"} · {kind ? KIND_META[kind]?.label : "all types"}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => exportLeads("xlsx")} disabled={!!exporting}
            className="gap-1 h-8 text-xs" data-testid="leads-export-xlsx">
            <MicrosoftExcelLogo size={14} weight="bold" className="text-emerald-600" /> {exporting === "xlsx" ? "Exporting…" : "Excel"}
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportLeads("pdf")} disabled={!!exporting}
            className="gap-1 h-8 text-xs" data-testid="leads-export-pdf">
            <FilePdf size={14} weight="bold" className="text-rose-600" /> {exporting === "pdf" ? "Exporting…" : "PDF"}
          </Button>
        </div>
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="label-caps border-b border-border bg-secondary/40">
              <th className="text-left p-3">Received</th>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Contact</th>
              <th className="text-left p-3">Company</th>
              <th className="text-center p-3">Type</th>
              <th className="text-left p-3">Source / Score</th>
              <th className="text-left p-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">Loading…</td></tr>
            ) : total === 0 ? (
              <tr><td colSpan={8} className="p-0"><Empty text="No leads yet. They'll appear here as visitors submit forms on your site." /></td></tr>
            ) : leads.map((l, i) => {
              const km = KIND_META[l.kind] || KIND_META.demo;
              const KIcon = km.icon;
              const isOpen = expanded === l.lead_id;
              return (
                <>
                <tr key={l.lead_id} className="border-b border-border last:border-0 align-top hover:bg-secondary/20 cursor-pointer" data-testid={`leads-row-${i}`} onClick={() => setExpanded(isOpen ? null : l.lead_id)}>
                  <td className="p-3 whitespace-nowrap text-muted-foreground">{fmtDateTime(l.created_at)}</td>
                  <td className="p-3 whitespace-nowrap font-medium">{l.name}</td>
                  <td className="p-3 whitespace-nowrap text-xs">
                    <a href={`mailto:${l.email}`} onClick={(ev) => ev.stopPropagation()} className="inline-flex items-center gap-1 text-[#0B3D91] hover:underline" data-testid={`leads-email-${i}`}>
                      <EnvelopeSimple size={13} weight="bold" /> {l.email}
                    </a>
                    {l.phone ? (
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        <a href={`tel:${l.phone.replace(/\s/g, "")}`} onClick={(ev) => ev.stopPropagation()}
                          className="inline-flex items-center gap-1 text-muted-foreground hover:text-[#0B3D91] hover:underline" data-testid={`leads-call-${i}`}>
                          <PhoneCall size={13} weight="bold" /> {l.phone}
                        </a>
                        <a href={`https://wa.me/${waNumber(l.phone)}?text=${waText(l.name, l.kind)}`} target="_blank" rel="noreferrer"
                          onClick={(ev) => ev.stopPropagation()}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-[#25D366]/12 text-[#128C4B] font-semibold hover:bg-[#25D366]/20 transition-colors"
                          data-testid={`leads-whatsapp-${i}`} title="Message on WhatsApp">
                          <WhatsappLogo size={13} weight="fill" /> WhatsApp
                        </a>
                        {l.whatsapp_opt_in ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-[#25D366]/12 text-[#128C4B] text-[10px] font-bold uppercase tracking-wide" data-testid={`leads-wa-pref-${i}`} title="Lead prefers WhatsApp">
                            <WhatsappLogo size={11} weight="fill" /> Prefers WhatsApp
                          </span>
                        ) : null}
                      </div>
                    ) : (
                      <div className="mt-1 text-[11px] text-muted-foreground italic" data-testid={`leads-nophone-${i}`}>No phone provided</div>
                    )}
                  </td>
                  <td className="p-3">{l.company || "—"}{l.plan ? <div className="text-[11px] text-muted-foreground">Plan: {l.plan}</div> : null}</td>
                  <td className="p-3 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-sm ${km.cls}`}>
                      <KIcon size={12} weight="bold" /> {km.label}
                    </span>
                  </td>
                  <td className="p-3 whitespace-nowrap" data-testid={`leads-score-${i}`}>
                    {l.channel ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-sm bg-[#0B3D91]/10 text-[#0B3D91]">{l.channel}</span> : <span className="text-muted-foreground text-xs">—</span>}
                    <div className="mt-1 flex items-center gap-1.5">
                      <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-sm ${TEMP_CLS[l.temperature] || TEMP_CLS.Cold}`}>{l.temperature || "Cold"}</span>
                      <span className="text-[11px] text-muted-foreground mono">{l.lead_score || 0} pts</span>
                    </div>
                  </td>
                  <td className="p-3 whitespace-nowrap" onClick={(ev) => ev.stopPropagation()}>
                    <Select value={l.status || "new"} onValueChange={(v) => changeStatus(l, v)} disabled={updating === l.lead_id}>
                      <SelectTrigger className={`h-7 w-32 rounded-sm text-xs font-semibold capitalize border-0 ${STATUS_CLS[l.status] || STATUS_CLS.new}`} data-testid={`leads-status-${i}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (<SelectItem key={s} value={s} className="capitalize" data-testid={`leads-status-${i}-${s}`}>{s.replace("_", " ")}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
                {isOpen && (
                  <tr className="bg-secondary/20 border-b border-border" data-testid={`leads-timeline-${i}`}>
                    <td colSpan={8} className="p-4">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <div className="label-caps mb-2">Activity Timeline</div>
                          {(l.timeline || []).length === 0 ? <div className="text-xs text-muted-foreground">No activity yet.</div> : (
                            <ol className="space-y-1.5">
                              {(l.timeline || []).map((ev, k) => (
                                <li key={k} className="flex items-center gap-2 text-xs">
                                  <span className="w-2 h-2 rounded-full bg-[#0B3D91]" />
                                  <span className="font-medium">{ev.label || ev.type}</span>
                                  <span className="text-muted-foreground ml-auto">{fmtDateTime(ev.at)}</span>
                                </li>
                              ))}
                            </ol>
                          )}
                        </div>
                        <div>
                          <div className="label-caps mb-2">Attribution</div>
                          <dl className="text-xs space-y-1">
                            {[["Channel", l.channel], ["UTM Source", l.attribution?.utm_source], ["UTM Campaign", l.attribution?.utm_campaign],
                              ["Landing", l.attribution?.landing_page], ["Referrer", l.attribution?.referrer || "Direct"],
                              ["Device", [l.attribution?.device, l.attribution?.os, l.attribution?.browser].filter(Boolean).join(" · ")],
                              ["Location", [l.attribution?.city, l.attribution?.country].filter(Boolean).join(", ")],
                              ["IP", l.attribution?.ip], ["Timezone", l.attribution?.timezone]].map(([k2, v]) => (
                                <div key={k2} className="flex gap-2"><dt className="text-muted-foreground w-28 shrink-0">{k2}</dt><dd className="font-medium break-all">{v || "—"}</dd></div>
                              ))}
                          </dl>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
